import { useEffect, useMemo, useRef, useState } from 'react';

type StateSetterPair<S> = [
  S,
  (newValue: S) => void,
  {
    /** inSync is true when the local state is in sync with the original state */
    inSync: boolean;
  },
];

// Options for useOptimisticState
interface OptimisticStateOptions {
  delayMs: number;
}

/**
 * useOptimisticState is a hook that allows you to update a stateful value immediately, but only call the original
 * setter after a delay. This is useful for optimistic UI updates, where you want to immediately update the UI, but
 * only commit the change to the server after a delay.
 */
export function useOptimisticState<S>(
  originalState: S,
  setOriginalState: (newValue: S) => void,
  options: OptimisticStateOptions
): StateSetterPair<S> {
  const { delayMs } = options;

  // State to store the local optimistic value
  const [state, setState] = useState<S>(originalState);

  // Ref to store the ID of the timeout
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const lastUpdate = useRef<number | null>(null);

  // Effect to synchronize with the original hook's state
  useEffect(() => {
    setState(originalState);
  }, [originalState]);

  // Custom setter function
  const setOptimisticState = (newValue: S) => {
    // Clear any existing timeout to ensure only the latest update is applied
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // Update the local optimistic state immediately
    setState(newValue);

    // Call the original setter after a delay
    timeoutId.current = setTimeout(() => {
      setOriginalState(newValue);
      timeoutId.current = null;
    }, delayMs);
    lastUpdate.current = performance.now();
  };

  const payload = useMemo(() => {
    return {
      inSync: originalState === state,
    };
  }, [originalState, state]);

  if (payload.inSync && lastUpdate.current) {
    lastUpdate.current = null;
  }

  // Clear timeout when umounting
  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  return [state, setOptimisticState, payload];
}
